import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Box, Flex, Text, Heading, Container } from "@radix-ui/themes";
import { Home, ListChecks, Wallet, BookOpen, User, LogOut, Sprout } from "lucide-react";
import { useUser, useClerk } from "@clerk/clerk-react";

const items = [
  { to: "/beranda", label: "Beranda", icon: Home },
  { to: "/utang", label: "Utang", icon: ListChecks },
  { to: "/cashflow", label: "Cashflow", icon: Wallet },
  { to: "/belajar", label: "Belajar", icon: BookOpen },
  { to: "/profil", label: "Profil", icon: User },
];

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { signOut } = useClerk();

  if (user && user.unsafeMetadata?.onboarded === false) {
    if (location.pathname !== "/onboarding") {
      navigate("/onboarding", { replace: true });
    }
  }

  return (
    <Box style={{ minHeight: "100vh", background: "var(--color-background)" }}>
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex"
        style={{
          position: "fixed",
          insetInlineStart: 0,
          insetBlock: 0,
          width: 240,
          background: "var(--color-surface)",
          borderRight: "1px solid var(--gray-a4)",
          flexDirection: "column",
          padding: "20px 12px",
          zIndex: 20,
        }}
      >
        <Flex align="center" gap="2" mb="6" px="2">
          <Box style={{
            width: 36, height: 36, borderRadius: 12,
            background: "var(--green-3)", color: "var(--green-11)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20,
          }}>🌱</Box>
          <Box>
            <Text weight="bold" size="3" style={{ lineHeight: 1.1 }}>BRIBA</Text>
            <Text size="1" color="gray">Bebas Riba</Text>
          </Box>
        </Flex>

        <nav style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                style={({ isActive }) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  textDecoration: "none",
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? "var(--green-11)" : "var(--gray-12)",
                  background: isActive ? "var(--green-3)" : "transparent",
                })}
              >
                <Icon size={18} strokeWidth={2.2} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <Box mt="auto" pt="4" style={{ borderTop: "1px solid var(--gray-a4)" }}>
          <Flex align="center" gap="2" px="2" py="2" mb="2">
            <Box style={{
              width: 32, height: 32, borderRadius: 16,
              background: "var(--green-3)", color: "var(--green-11)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: 700,
            }}>
              {(user?.firstName?.[0] ?? "U").toUpperCase()}
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Text size="2" weight="medium" truncate>
                {user?.firstName ?? user?.username ?? "Kamu"}
              </Text>
              <Text size="1" color="gray" truncate>
                {user?.phoneNumbers?.[0]?.phoneNumber ?? ""}
              </Text>
            </Box>
          </Flex>
          <button
            onClick={() => signOut()}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              width: "100%", padding: "8px 12px", borderRadius: 10,
              background: "transparent", border: "none",
              color: "var(--gray-11)", fontSize: 13, cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--gray-3)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <LogOut size={16} />
            Keluar
          </button>
        </Box>
      </aside>

      {/* Main content */}
      <Box className="md:ps-[240px]" style={{ minHeight: "100vh" }}>
        <Container
          size="3"
          px={{ initial: "4", md: "6" }}
          py={{ initial: "5", md: "7" }}
          style={{ maxWidth: 980 }}
        >
          <Box style={{ paddingBottom: 96 }} className="md:pb-0">
            <Outlet />
          </Box>
        </Container>
      </Box>

      {/* Mobile bottom nav */}
      <nav
        aria-label="Navigasi utama"
        className="md:hidden"
        style={{
          position: "fixed",
          insetInline: 0,
          bottom: 0,
          zIndex: 30,
          background: "var(--color-panel-solid)",
          borderTop: "1px solid var(--gray-a5)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <Box style={{ maxWidth: 480, margin: "0 auto", display: "flex", height: 64 }}>
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                style={({ isActive }) => ({
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  textDecoration: "none",
                  fontSize: 11,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? "var(--green-11)" : "var(--gray-10)",
                })}
              >
                <Icon size={20} strokeWidth={2.2} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </Box>
      </nav>
    </Box>
  );
}
